import { router } from "@inertiajs/react";
import type ListingsPhoto from "@travily/types/models/ListingsPhoto";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type UploadFile, useDirectUploadMultiple } from "@/hooks/use-direct-upload-multiple";

type StagedPhoto =
  | { kind: "existing"; photo: ListingsPhoto }
  | { kind: "new"; localId: string; previewUrl: string; signedId: string };

/**
 * One entry of the full final set the section PATCH takes: array order is the new
 * position, `signed_id` attaches a blob, and existing photos left out are deleted.
 */
type PhotoSaveEntry = { id: string } | { signed_id: string };

interface UseStagedPhotoManagerProps {
  photos: ListingsPhoto[];
  saveUrl: string;
}

type CompletedUpload = UploadFile & { signedId: string };

function stagedId(entry: StagedPhoto): string {
  return entry.kind === "existing" ? entry.photo.id : entry.localId;
}

function toStaged(photos: ListingsPhoto[]): StagedPhoto[] {
  return photos.map((photo) => ({ kind: "existing" as const, photo }));
}

function toListingPhoto(entry: StagedPhoto, index: number): ListingsPhoto {
  const position = index + 1;
  if (entry.kind === "existing") return { ...entry.photo, position };

  return {
    id: entry.localId,
    image: {
      src: entry.previewUrl,
      srcset: entry.previewUrl,
      webp_srcset: "",
      // Real dimensions only exist once the server has analysed the blob; 1:1
      // matches the square tile the organiser crops the preview into.
      width: 1,
      height: 1,
      alt: "",
      blurhash: null,
    },
    position,
  };
}

function toSaveEntry(entry: StagedPhoto): PhotoSaveEntry {
  return entry.kind === "existing" ? { id: entry.photo.id } : { signed_id: entry.signedId };
}

function isCompleted(file: UploadFile): file is CompletedUpload {
  return file.status === "completed" && file.signedId !== null;
}

/**
 * Buffered counterpart to `usePhotoManager`: reorder, delete and upload stage
 * locally and only reach the published listing when `save` PATCHes the whole
 * set. The controller surface is the same one the photo field components
 * consume, plus `isDirty` / `isSaving` / `save` for the editor's Save dock.
 */
export function useStagedPhotoManager({ photos, saveUrl }: UseStagedPhotoManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [staged, setStaged] = useState<StagedPhoto[]>(() => toStaged(photos));
  const previewUrlsRef = useRef(new Set<string>());
  const upload = useDirectUploadMultiple();

  const revokePreview = useCallback((url: string) => {
    previewUrlsRef.current.delete(url);
    URL.revokeObjectURL(url);
  }, []);

  const revokeAllPreviews = useCallback(() => {
    for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
    previewUrlsRef.current.clear();
  }, []);

  useEffect(() => revokeAllPreviews, [revokeAllPreviews]);

  // Re-stage only when the SERVER set changes (a new `photos` reference, e.g.
  // the redirect after a successful save), never when local edits diverge.
  const serverPhotosRef = useRef(photos);
  useEffect(() => {
    if (serverPhotosRef.current === photos) return;
    serverPhotosRef.current = photos;
    revokeAllPreviews();
    setStaged(toStaged(photos));
  }, [photos, revokeAllPreviews]);

  const openModal = useCallback(() => {
    upload.reset();
    setIsModalOpen(true);
  }, [upload]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    upload.reset();
  }, [upload]);

  const confirmUpload = useCallback(() => {
    const completed = upload.files.filter(isCompleted);
    if (completed.length === 0) return;

    // `closeModal` resets the upload hook, which revokes the object URLs it
    // created — staged previews need their own.
    const additions = completed.map((file): StagedPhoto => {
      const previewUrl = URL.createObjectURL(file.file);
      previewUrlsRef.current.add(previewUrl);
      return { kind: "new", localId: `staged-${file.id}`, previewUrl, signedId: file.signedId };
    });

    setStaged((prev) => [...prev, ...additions]);
    closeModal();
  }, [upload.files, closeModal]);

  const reorderPhotos = useCallback((orderedIds: string[]) => {
    setStaged((prev) => {
      const byId = new Map(prev.map((entry) => [stagedId(entry), entry]));
      const next = orderedIds.map((id) => byId.get(id)).filter((entry): entry is StagedPhoto => entry !== undefined);
      return next.length === prev.length ? next : prev;
    });
  }, []);

  // Unconfirmed: deleting a staged entry is reversible until Save, so it
  // applies on request. The creator wizard keeps its confirmation — there a
  // delete hits the live listing immediately.
  const removePhoto = useCallback(
    (photoId: string) => {
      const target = staged.find((entry) => stagedId(entry) === photoId);
      if (target?.kind === "new") revokePreview(target.previewUrl);

      setStaged((prev) => prev.filter((entry) => stagedId(entry) !== photoId));
    },
    [revokePreview, staged],
  );

  const isDirty = useMemo(() => {
    if (staged.length !== photos.length) return true;
    return staged.some((entry, index) => stagedId(entry) !== photos[index]?.id);
  }, [photos, staged]);

  const save = useCallback(() => {
    router.patch(
      saveUrl,
      { photos: staged.map(toSaveEntry) },
      {
        preserveScroll: "errors",
        preserveState: "errors",
        onStart: () => setIsSaving(true),
        onFinish: () => setIsSaving(false),
      },
    );
  }, [saveUrl, staged]);

  return {
    photos: useMemo(() => staged.map(toListingPhoto), [staged]),
    upload,
    isModalOpen,
    openModal,
    closeModal,
    confirmUpload,
    reorderPhotos,
    removePhoto,
    isDirty,
    isSaving,
    save,
  };
}
